import Vue from 'vue'
import camelcaseKeys from 'camelcase-keys'
import staticChoices from '@/json/staticChoices.json'
import { VueGotoInstance } from '@/plugins/gotowebinars'

import GET_FOLDERS from '@/graphql/queries/getFolders.gql'
import GET_FOLDERS_BY_CODE from '@/graphql/queries/getFoldersByCode.gql'
import { FOLDER_COLORS } from '@/config/constants'
import { useAssessmentStore } from '@/stores/useAssessmentStore'
import { api } from '@/services/api'
import { useDrf } from '@/services/featureFlags'

export default {
    fetchStaticChoices(store) {
        if (Vue.auth.check() && (!store.state.staticChoices || Object.keys(store.state.staticChoices).length === 0 || Object.keys(store.state.staticChoices).length === 1)){
            // if we just logged in and need to get the full static choices list
            api.getRaw('static.choices/').then((data) => {
                let newChoices = {}
                const keys = Object.keys(data)
                for (let key of keys) {
                    if (typeof data[key] === "number"){
                        newChoices[key] = {
                            value: data[key],
                            label: key,
                            text: key,
                        }
                        continue
                    }
                    newChoices[key] = data[key].map((item) => {
                        // BE emits {value, label, ui_label} dicts for
                        // keys that carry a ui_label (survey_visit.type,
                        // assessment.type). Every other key still emits
                        // the legacy [value, label] 2-tuple.
                        if (item && typeof item === 'object' && !Array.isArray(item)) {
                            return {
                                value: item.value,
                                label: item.label,
                                text: item.label,
                                uiLabel: item.ui_label || item.label,
                            }
                        }
                        return {
                            value: item[0],
                            label: item[1],
                            text: item[1],
                        }
                    })
                }

                store.commit('SET_STATIC_OBJECT', newChoices)
            }).catch(err => {
                console.error(err)
            })
        } else if (!Vue.auth.check() && Object.keys(store.state.staticChoices).length === 0) {
            // if we are not logged in then get the default static choices - which should only have the user title
            const data = staticChoices
            let newChoices = {}
            const keys = Object.keys(data)
            for (let key of keys) {
                newChoices[key] = data[key].map((item) => {
                    return {
                        value: item[0],
                        label: item[1],
                        text: item[1],
                    }
                })
            }
            store.commit('SET_STATIC_OBJECT', newChoices)
        }
    },
    getPermissions(state, { practiceId }) {
        const permissionsStore = useAssessmentStore()
        const { updateAssessmentPermissions } = permissionsStore
        if (practiceId) {
            api.get(`web.practice/${practiceId}/user_permissions/`)
                .then((data) => {
                    const permissions = data?.permissions
                    state.commit('SET_PERMISSIONS', permissions)
                    updateAssessmentPermissions(permissions?.assessment)
                })
                .catch((err) => {
                    console.log('something went wrong', err)
                    // if we didn't get the permissions then it means we can't view this practice
                    Vue.router.push({name: 'PracticeList'})
                })
        }
    },
    setPracticeId($store, { practiceId }) {
        if (practiceId !== $store.state.practiceId) {
            $store.commit('SET_STAFF_TOTAL', 0)
            $store.commit('SET_DOCS_TOTAL', 0)

            $store.commit('SET_STAFF_FIRST_VIEW', true)
            $store.commit('SET_DOCUMENTS_FIRST_VIEW', true)
            $store.commit('SET_ACCREDITATION_FIRST_VIEW', true)
        }

        $store.commit('SET_PRACTICE_ID', practiceId)
    },
    async getGoToWebinarAccessToken($store) {
        const code = $store.state.webinarLoginCode
        if (code) {
            try {
                const params = {
                    grant_type: 'authorization_code',
                    code: code,
                    redirect_uri: `${window.location.origin}/GoToWebinars/callback`
                }
                const response = await VueGotoInstance.post(
                    '/oauth/v2/token', null, {
                        params: params,
                        headers: {
                            Authorization: `Basic ${process.env.VUE_APP_GOTOWEBINAR_BASIC_AUTH}`,
                            Accept: 'application/json',
                            'Content-Type': 'application/x-www-form-urlencoded'
                        }
                    }
                )
    
                $store.commit('SET_WEBINAR_ACCESS_OBJECT', response.data)
            } catch (err) {
                console.log('error from getting webinar access object', err)
                //TODO: handle error and try again or inform user to refresh or retry the connection to GoToWebinar
            }
        }
    },
    /**
     * @deprecated Use fetchFoldersByCode instead and create a new function if more filters are needed
     * TODO: need to move to REST call for all these as well
     * @param {*} store 
     * @param {*} param1 
     * @returns 
     */
    fetchFolders(store, {apolloClient, getVisibleFolders, practiceId, emptyHidden}) {
        // if variable is null, then we assume we want to see the folders so we make it true
        if (getVisibleFolders == null) getVisibleFolders = !getVisibleFolders
        store.commit('SET_GET_VISIBLE_FOLDERS', getVisibleFolders)

        if (useDrf('getFolders')) {
            // §H.2.2a — FolderViewset honours the same where-clause
            // shape as the Hasura query. api.js camelcases the
            // response; we then colourise per row to match the
            // existing FE contract.
            return api.get('web.folder/', {
                practiceId,
                visible: !!getVisibleFolders,
                emptyHidden,
            }).then((data) => {
                const folders = (data || []).map((folder, index) => ({
                    ...folder,
                    color: FOLDER_COLORS[index % FOLDER_COLORS.length],
                }))
                store.commit('SET_FOLDERS', folders)
            })
        }

        return apolloClient.query({
            query: GET_FOLDERS,
            variables: { practiceId, visible: !!getVisibleFolders, emptyHidden }
        }).then(({ data }) => {
            const folders = camelcaseKeys(data.folders, { deep: true })

            folders.forEach((folder, index) => {
                folder.color = FOLDER_COLORS[index % FOLDER_COLORS.length]
            })

            store.commit('SET_FOLDERS', folders)
        })
    },
    async fetchFoldersByCode({ commit }, { apolloClient, folderCode }) {
        if (useDrf('getFoldersByCode')) {
            // §H.2.2a — same FolderViewset endpoint with ?code=.
            const data = await api.get('web.folder/', { code: folderCode })
            const folders = (data || []).map((folder, index) => ({
                ...folder,
                color: FOLDER_COLORS[index % FOLDER_COLORS.length],
            }))
            commit('SET_FOLDERS', folders)
            return
        }
        const { data } = await apolloClient.query({
          query: GET_FOLDERS_BY_CODE,
          variables: { code: folderCode }
        })
        const folders = camelcaseKeys(data.folders, { deep: true })

        folders.forEach((folder, index) => {
            folder.color = FOLDER_COLORS[index % FOLDER_COLORS.length]
        })

        commit('SET_FOLDERS', folders)
    },
    showModal({ commit }, { payload, modalName}) {
        commit('MODAL_IS_VISIBLE', true)
        commit('MODAL_DATA', payload)
        commit('MODAL_COMPONENT_NAME', modalName)
    },
    hideModal({ commit }) {
        commit('MODAL_IS_VISIBLE', false)
        commit('MODAL_COMPONENT_NAME', '')
        commit('MODAL_DATA', {})
    }
}
